<script lang="ts">
  import { Skeleton } from '@/lib/components/ui/skeleton';
  import { cn } from '@/lib/utils';
  import { XSSDeviationCategory, xssDeviationToCategory, type XSSDetails, type XSSDeviationDetails } from '@/Util/utils';
  import * as Popover from '$lib/components/ui/popover';
  import { Button } from '@/lib/components/ui/button';
  import XSSTableForXSSCircle from './XSSTableForXSSCircle.svelte';
  import { json } from 'svelte-i18n';
  import { xssColors, type FocusDescription } from '@/Common/constants';

  export let primaryXSSDetails: XSSDetails<number>;
  export let targetXSSDetails: XSSDetails<number> | null = null;
  export let primaryTitle: 'Forecasted' | 'Scheduled' | 'Completed';
  export let secondaryTitle: 'Forecasted' | 'Scheduled' | null = null;

  export let loading: boolean = false;
  export let small: boolean = false;

  export let focus: string = null;
  export let specRating: string = null;

  let xssDeviationCategory: XSSDetails<XSSDeviationDetails> | null = null;
  $: {
    if (primaryXSSDetails && targetXSSDetails) {
      xssDeviationCategory = xssDeviationToCategory(primaryXSSDetails, targetXSSDetails);
    } else {
      xssDeviationCategory = null;
    }
  }

  let spfDesc: [string, number | null] | null = null;
  $: if (specRating && focus) {
    spfDesc = generateSpecificFocusDescription(specRating, focus, primaryXSSDetails.xhss);
  }

  const suitabilityColor = (suitability: XSSDeviationCategory): string => {
    switch (suitability) {
      case XSSDeviationCategory.Good:  return xssColors.deviationGood;
      case XSSDeviationCategory.Warn:  return xssColors.deviationWarn;
      case XSSDeviationCategory.Alert: return xssColors.deviationAlert;
      default:                         return '#707377';
    }
  };

  const getCircleClasses = (xssDeviationCategory: XSSDetails<XSSDeviationDetails> | null): string => {
    if (!xssDeviationCategory) return 'border-[#707377]';
    return 'border-transparent text-white hover:text-white';
  };

  // Returns an SVG path string for a pie slice from startDeg to endDeg (0 = top, clockwise)
  const pieSlicePath = (startDeg: number, endDeg: number): string => {
    const toRad = (deg: number) => (deg - 90) * Math.PI / 180;
    const x1 = 50 + 50 * Math.cos(toRad(startDeg));
    const y1 = 50 + 50 * Math.sin(toRad(startDeg));
    const x2 = 50 + 50 * Math.cos(toRad(endDeg));
    const y2 = 50 + 50 * Math.sin(toRad(endDeg));
    const largeArc = endDeg - startDeg > 180 ? 1 : 0;
    return `M50,50 L${x1},${y1} A50,50 0 ${largeArc},1 ${x2},${y2} Z`;
  };

  const generateSpecificFocusDescription = (specificity: string, focus: string, xhss: number): [string, number | null] | null => {
    const sp = specificity.toLowerCase();
    const foc = focus.toLowerCase();

    const focusLocale = $json('focus');
    let details: FocusDescription | undefined = focusLocale[foc];

    if (!details) return null;

    const specificityLocale = details.running ? $json('specificity_rating_running') : $json('specificity_rating');

    let spDesc: string | undefined = specificityLocale[sp]?.description;

    if (!spDesc) return null;

    if (details.endurance_alt_description && xhss != 0) {
      details.description = details.endurance_alt_description;
    } else if (details.endurance_alt_description && xhss == 0) {
      spDesc = '';
    }

    return [`${details.description} ${spDesc}`, details.minute_power];
  };

</script>

{#if loading}
  <Skeleton class={cn('rounded-full aspect-square shrink-0', small ? 'w-[34px]' : 'w-[50px]')} />
{:else}
  <Popover.Root portal="#shadcn-ui-dialog-target">
    <Popover.Trigger asChild let:builder>
      <Button
        variant="outline"
        class={cn('relative overflow-hidden p-0 flex flex-col text-center shrink-0 rounded-full bg-transparent border-[2px] border-solid transition-[filter] duration-[0.05s] hover:brightness-75',
          small ? 'w-[34px] h-[34px]' : 'w-[50px] h-[50px]',
          getCircleClasses(xssDeviationCategory),
        )}
        builders={[builder]}
        on:click={(e) => {
          e.stopPropagation();
        }}
      >
        {#if xssDeviationCategory}
          <svg viewBox="0 0 100 100" class="absolute inset-0 w-full h-full" aria-hidden="true" style="background: transparent;">
            <path d={pieSlicePath(0, 120)}   fill={suitabilityColor(xssDeviationCategory.xlss.suitability)} />
            <path d={pieSlicePath(120, 240)} fill={suitabilityColor(xssDeviationCategory.xhss.suitability)} />
            <path d={pieSlicePath(240, 360)} fill={suitabilityColor(xssDeviationCategory.xpss.suitability)} />
          </svg>
        {/if}
        <h2 class={cn('relative z-10 font-bold my-0', small ? 'text-[0.8rem]' : 'text-[1.1rem]')}>{Math.round(primaryXSSDetails?.xss ?? 0)}</h2>
        <h5 class={cn('relative z-10 m-0', small ? 'text-[0.5rem]' : 'text-[0.6rem]')}>XSS</h5>
      </Button>
    </Popover.Trigger>
    <Popover.Content side="top" align="center" class="w-auto">
      <div class="max-w-[270px]">
        <div class="flex flex-row gap-x-4 text-black">
          {#if targetXSSDetails}
            <XSSTableForXSSCircle title={secondaryTitle} xssDetails={targetXSSDetails} includeRowNames />
          {/if}
          <XSSTableForXSSCircle title={primaryTitle} xssDetails={primaryXSSDetails} {xssDeviationCategory} includeRowNames={!targetXSSDetails} />
          {#if specRating && focus}
            <div class="py-2 w-max h-max m-auto">
              <h4 class="my-0 mx-auto font-bold text-center">
                {specRating + ' ' + focus}
              </h4>
              {#if spfDesc?.at(1)}
                <p class="mb-0 mt-1 w-max mx-auto">({spfDesc?.at(1)} Minute Power)</p>
              {/if}
            </div>
          {/if}
        </div>
        {#if spfDesc?.at(0)}
          <p class="max-w-full text-center mb-0 !mt-1 mx-auto">
            {spfDesc?.at(0)}
          </p>
        {/if}
      </div>
    </Popover.Content>
  </Popover.Root>
{/if}
